import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface SearchResult {
  type: 'city' | 'state';
  text: string;
}

interface LocationResult {
  city: string;
  state: string;
}

export default function SearchBar() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchLocations = async () => {
      if (!query.trim() || query.length < 2) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      try {
        // Search for cities and states
        const { data } = await supabase
          .from('locations')
          .select('city, state')
          .or(`city.ilike.%${query}%,state.ilike.%${query}%`)
          .limit(10) as { data: LocationResult[] | null };

        const searchResults: SearchResult[] = [];
        const uniqueCities = new Set<string>();
        const uniqueStates = new Set<string>();

        data?.forEach((item: LocationResult) => {
          // Add city if it matches search and isn't already added
          if (item.city.toLowerCase().includes(query.toLowerCase())) {
            const cityKey = `${item.city}, ${item.state}`;
            if (!uniqueCities.has(cityKey)) {
              uniqueCities.add(cityKey);
              searchResults.push({
                type: 'city',
                text: cityKey
              });
            }
          }

          // Add state if it matches search and isn't already added
          if (item.state.toLowerCase().includes(query.toLowerCase())) {
            if (!uniqueStates.has(item.state)) {
              uniqueStates.add(item.state);
              searchResults.push({
                type: 'state',
                text: item.state
              });
            }
          }
        });

        setResults(searchResults);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimeout = setTimeout(searchLocations, 300);
    return () => clearTimeout(debounceTimeout);
  }, [query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setShowResults(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'state') {
      navigate(`/reiki/${result.text.toLowerCase().replace(/\s+/g, '-')}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(result.text)}`);
    }
    setShowResults(false);
    setQuery('');
  };

  return (
    <div ref={searchRef} className="w-full max-w-3xl mx-auto relative">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
            placeholder="Search by city or state..."
            className="w-full px-4 py-3 pl-12 text-lg rounded-lg border border-gray-300 dark:border-dark-accent bg-white dark:bg-dark-secondary text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            autoComplete="off"
          />
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-5 w-5" />
          
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setResults([]);
              }}
              className="absolute right-20 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-purple-600 dark:bg-purple-500 text-white rounded-md hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors duration-200"
          >
            Search
          </button>
        </div>
      </form>

      {/* Search Results Dropdown */}
      {showResults && (query.length > 1) && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-dark-secondary rounded-lg shadow-lg border border-gray-200 dark:border-dark-accent overflow-hidden">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500 mx-auto"></div>
            </div>
          ) : results.length > 0 ? (
            <ul className="max-h-96 overflow-auto">
              {results.map((result, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleResultClick(result)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-accent/50 flex items-center justify-between"
                  >
                    <span className="text-gray-900 dark:text-white">
                      {result.text}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {result.type}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : query.trim() ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              No results found
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}